'use client';

import { useMemo, useState } from 'react';
import { AddToListModal, MedRMStatus, PushToCRMGroup } from 'src/components';
import ProspectListAndTagContent from './ProspectListAndTagContent';
import { useParams } from 'next/navigation';
import { useGetMappingsQuery } from 'src/api/redux';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';

interface ProfileHeaderProps {
  children: React.ReactNode;
  contentType: string;
}

const ProfileHeader = ({ children, contentType }: ProfileHeaderProps) => {
  const { type } = useParams<{ type: string }>();
  const [showAddToListModal, setShowAddToListModal] = useState(false);
  const { details, refetch } = useProfile();
  const isHcpGroup = contentType === 'hcp-group';

  const { data: allMappings } = useGetMappingsQuery({
    sort: 'label',
    order: 'asc',
    page: 0,
    pageSize: 100,
  });

  const mappings = useMemo(() => {
    return allMappings?.results?.filter(
      ({ medscout_object_type, enabled }) =>
        medscout_object_type?.toLowerCase() === type && enabled
    );
  }, [allMappings?.results, type]);

  if (!details) return null;

  return (
    <div className="container mx-auto px-4 max-w-screen-xl">
      <div className="mt-1 mb-2">
        <div className="text-lg text-primary">{children}</div>
        <div
          className={`flex items-center mt-1 ${
            mappings?.length > 0 ? 'justify-between' : `justify-end`
          }`}
        >
          {mappings?.length > 0 && (
            <>
              <PushToCRMGroup provider={details} />
              <MedRMStatus provider={details} />
            </>
          )}
          <div className="flex items-center gap-2 flex-grow-0">
            {!isHcpGroup && (
              <div
                onClick={() => setShowAddToListModal(true)}
                className="flex items-center bg-white border border-[#CDD7E1] cursor-pointer hover:bg-[#EFEFF1] overflow-hidden gap-1 rounded-tl-2xl rounded-br-2xl pl-2"
              >
                <Icon icon={faPlus} color="#636B74" size="sm" />
                <span className="whitespace-nowrap text-[0.65rem] font-normal rounded-tl-[0.95rem] px-4">
                  Add to List
                </span>
              </div>
            )}
            <ProspectListAndTagContent
              provider={details}
              hideProspectList={isHcpGroup}
            />
          </div>
        </div>
      </div>

      <AddToListModal
        isOpen={showAddToListModal}
        onClose={() => setShowAddToListModal(false)}
        mode="add"
        providers={[details]}
        onSuccess={() => refetch()}
      />
    </div>
  );
};

export default ProfileHeader;
